import { Box, Typography, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { JobStatsItem } from '../../../../types/dashboard';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import GenericTable from '../../../../shared/GenericTable';
import { getJobStatusChipTone, getReportChipSx } from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

type JobsCreatedJobsListProps = {
    jobs: JobStatsItem[];
};

export default function JobsCreatedJobsList({
    jobs,
}: JobsCreatedJobsListProps) {
    const theme = useTheme();

    const columns: MRT_ColumnDef<JobStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Job Ref',
                size: 100,
                Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            },
            {
                accessorKey: 'contractorName',
                header: 'Contractor',
                size: 140,
            },
            {
                accessorKey: 'ownerName',
                header: 'Host',
                size: 140,
            },
            {
                accessorKey: 'moduleName',
                header: 'Experience',
                size: 160,
            },
            {
                accessorKey: 'jobDate',
                header: 'Job Date',
                size: 120,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                id: 'services',
                accessorFn: (row) =>
                    row.services?.map(camelCaseToTitleCase).join(', ') ?? '—',
                header: 'Services',
                size: 180,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 130,
                Cell: ({ cell }) => (
                    <CustomChip
                        label={camelCaseToTitleCase(cell.getValue<string>())}
                        sx={getReportChipSx(
                            getJobStatusChipTone(cell.getValue<string>()),
                            theme,
                        )}
                        variant='outlined'
                    />
                ),
            },
            {
                accessorKey: 'createdAt',
                header: 'Created',
                size: 120,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
        ],
        [theme],
    );

    const rows = jobs ?? [];

    if (!rows.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual jobs found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={rows}
                tableName='Jobs Created Report'
                paginationItemLabel='jobs'
            />
        </Box>
    );
}
